import { AuthContext } from 'auth';
import {
  Alert,
  Button,
  FieldContainer,
  Modal,
  Table,
} from '@odin-labs/components';
import {
  type SiteControllerFieldsFragment,
  SiteReaderOwnership,
  useGetSiteUnresolvedReadersQuery,
  useRemoveSiteControllerMutation,
  useSyncSiteControllerMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import React from 'react';
import { getFriendlyFormattedDateTime } from 'utils/dates';
import { ReaderAttachments } from './ReaderAttachments';
import { HealthBadge, lastSyncText } from './DevicesTab.tables';
import {
  getReaderColumns,
  type SiteControllerReaderColumn,
} from './SiteControllerDrawer.tables';
import {
  lostResponseOutcome,
  removeOutcome,
  syncOutcome,
  thrownOutcome,
  type SiteDeviceOutcome,
} from './siteDeviceResults';
import {
  type ReloadSiteDevices,
  useSiteDeviceCommand,
} from './useSiteDeviceCommand';

type SiteControllerDrawerProps = {
  open: boolean;
  siteId: string;
  controller: SiteControllerFieldsFragment | null;
  /** Whether the user may sync and remove; the API checks the same catalog. */
  canManage: boolean;
  onClose: () => void;
  onAfterLeave: () => void;
  /** The site's devices changed, or may have: read them again. */
  onChanged: ReloadSiteDevices;
};

const classes = {
  section: 'odin-space-y-3',
  heading: 'odin-text-base odin-font-medium odin-text-gray-900',
  grid: 'odin-grid odin-grid-cols-6 odin-gap-4',
  value: 'odin-text-sm odin-text-gray-900',
  muted: 'odin-text-sm odin-text-gray-500',
  actions: 'odin-flex odin-items-center odin-gap-2',
};

const field = (
  name: string,
  label: string,
  value: React.ReactNode,
): React.ReactElement => (
  <FieldContainer
    key={name}
    name={name}
    label={label}
    className="odin-col-span-6 sm:odin-col-span-3"
  >
    <div className={classes.value}>{value}</div>
  </FieldContainer>
);

/**
 * One controller: identity, health, connection, ports and readers, with
 * Sync and Remove for a user who may manage the site's devices. Every
 * answer is shown as the API gave it; an answer that applied nothing
 * because the state moved reloads the site first, and a reload that
 * fails keeps the actions off until the drawer is opened again. What one
 * controller answered is never shown for another.
 */
export function SiteControllerDrawer({
  open,
  siteId,
  controller,
  canManage,
  onClose,
  onAfterLeave,
  onChanged,
}: SiteControllerDrawerProps): React.ReactElement | null {
  const auth = React.useContext(AuthContext);
  const last = React.useRef<SiteControllerFieldsFragment | null>(null);
  if (controller) last.current = controller;
  const shown = controller ?? last.current;
  const [confirmingRemoval, setConfirmingRemoval] = React.useState(false);
  const { outcome, setOutcome, busy, stale, run, reset } = useSiteDeviceCommand(
    { open, onChanged },
  );
  // Whatever the previous controller answered is gone once another is shown.
  const shownId = shown?.assignmentId;
  React.useEffect(() => {
    reset();
    setConfirmingRemoval(false);
  }, [shownId, reset]);
  const [sync, syncState] = useSyncSiteControllerMutation();
  const [remove] = useRemoveSiteControllerMutation();
  // The readers that would refuse a removal, read when one is asked for.
  const unresolvedQuery = useGetSiteUnresolvedReadersQuery({
    variables: { siteId },
    fetchPolicy: 'no-cache',
    skip: !confirmingRemoval,
  });
  const readerColumns = React.useMemo<SiteControllerReaderColumn[]>(
    () => getReaderColumns(shown?.name ?? ''),
    [shown?.name],
  );
  if (!shown) return null;
  const input = { siteId, assignmentId: shown.assignmentId };

  const explainDeparture = (
    result: SiteDeviceOutcome,
    controllers: SiteControllerFieldsFragment[],
  ): boolean => {
    if (
      controllers.some(
        ({ assignmentId }) => assignmentId === shown.assignmentId,
      )
    )
      return false;
    AlertService.alert(
      'warn',
      'Controller no longer on this site',
      `${result.message} The controller is no longer assigned here.`,
    );
    onClose();
    return true;
  };

  const runSync = (
    assignmentId = shown.assignmentId,
    name = shown.name,
  ): Promise<void> => {
    const aboutShown = assignmentId === shown.assignmentId;
    const announce = (result: SiteDeviceOutcome): void => {
      AlertService.alert(result.type, name, result.message);
    };
    const shownLeft = (
      controllers: SiteControllerFieldsFragment[] | undefined,
    ): boolean =>
      !!controllers &&
      !controllers.some(
        ({ assignmentId: openId }) => openId === shown.assignmentId,
      );
    return run({
      showsOn: aboutShown ? 'drawer' : 'toast',
      command: async () => {
        try {
          const { data } = await sync({
            variables: { input: { siteId, assignmentId } },
          });
          return data
            ? syncOutcome(data.syncSiteController)
            : lostResponseOutcome('sync');
        } catch (error) {
          return thrownOutcome('synced', 'sync', error);
        }
      },
      onReloaded: (result, controllers) => {
        if (aboutShown) {
          explainDeparture(result, controllers);
          return;
        }
        if (shownLeft(controllers)) {
          AlertService.alert(
            'warn',
            'Controller no longer on this site',
            `${shown.name} is no longer assigned here.`,
          );
          onClose();
          return;
        }
        announce(result);
      },
      onApplied: (result, controllers) => {
        if (aboutShown) {
          if (controllers && explainDeparture(result, controllers)) return;
          setOutcome(
            controllers
              ? result
              : {
                  ...result,
                  type: 'warn',
                  message: `${result.message} Could not reload the site; close and open it again before another attempt.`,
                },
          );
          return;
        }
        if (shownLeft(controllers)) {
          AlertService.alert(
            'warn',
            'Controller no longer on this site',
            `${shown.name} is no longer assigned here.`,
          );
          onClose();
          return;
        }
        announce(result);
      },
    });
  };

  const closeWith = (title: string, message: string): void => {
    onClose();
    AlertService.alert('success', title, message);
  };
  const runRemove = (): Promise<void> => {
    setConfirmingRemoval(false);
    return run({
      command: async () => {
        try {
          const { data } = await remove({ variables: { input } });
          return data
            ? removeOutcome(data.removeSiteController)
            : lostResponseOutcome('removal');
        } catch (error) {
          return thrownOutcome('removed', 'removal', error);
        }
      },
      onApplied: (result) => closeWith(result.title, result.message),
      onReloaded: (result, controllers) => {
        if (!result.lost) explainDeparture(result, controllers);
      },
      onLost: (controllers) => {
        if (
          !controllers.some(
            ({ assignmentId }) => assignmentId === input.assignmentId,
          )
        )
          closeWith(
            'Controller removed',
            'Odin did not answer, but the controller is no longer on this site.',
          );
      },
    });
  };

  const actionsEnabled = !busy && !stale;
  const close = (): void => {
    if (!busy) onClose();
  };
  const readersLeaving = shown.readers.filter(
    ({ ownership }) => ownership === SiteReaderOwnership.Assigned,
  );
  // What will refuse the removal, as far as the screen knows.
  const blockers = [
    ...readersLeaving
      .filter(({ placement }) => placement.published)
      .map(({ name }) => `${name} is placed in the published layout.`),
    ...(unresolvedQuery.data?.siteUnresolvedReaders ?? []).map(
      ({ name, controller: on }) =>
        on
          ? `${name}, on ${on.name}’s ${on.portLabel} port, has no recorded controller. Sync ${on.name} first.`
          : `${name} has no recorded controller; Support must resolve it.`,
    ),
  ];

  return (
    <>
      <Modal
        open={open}
        setOpen={(next) => !next && close()}
        afterLeave={onAfterLeave}
        overlayCloseDisabled={busy}
        variant="drawer"
        size="md"
        title={shown.name}
        subtitle={shown.serialNumber ?? undefined}
        actionsPanel={null}
        onCancel={close}
        cancelText="Close"
        cancelButtonEnabled={!busy}
      >
        <div className="odin-space-y-6">
          {canManage && (
            <div className={classes.actions}>
              <Button
                text="Sync from Avigilon"
                size="sm"
                theme="white"
                disabled={!actionsEnabled}
                withSpinner={syncState.loading}
                onClick={() => runSync()}
              />
              <Button
                text="Remove from site"
                size="sm"
                theme="danger"
                disabled={!actionsEnabled}
                onClick={() => setConfirmingRemoval(true)}
              />
            </div>
          )}
          {outcome && (
            <Alert
              type={outcome.type}
              text={`${outcome.title}. ${outcome.message}`}
            />
          )}
          <section className={classes.section}>
            <h3 className={classes.heading}>Controller</h3>
            <div className={classes.grid}>
              {field('health', 'Health', <HealthBadge controller={shown} />)}
              {field('lastSync', 'Last sync', lastSyncText(shown))}
              {field('model', 'Model', shown.modelKey ?? 'Not recorded')}
              {field(
                'connection',
                'Avigilon',
                shown.connection
                  ? `${shown.connection.externalName} (${shown.connection.externalId})`
                  : 'Not connected',
              )}
              {field(
                'observed',
                'Health observed',
                shown.healthObservedAt
                  ? getFriendlyFormattedDateTime(shown.healthObservedAt)
                  : 'Never',
              )}
              {field(
                'added',
                'Recorded',
                getFriendlyFormattedDateTime(shown.createdAt),
              )}
              {field(
                'assigned',
                'Assigned to this site',
                shown.assignedAt
                  ? `${getFriendlyFormattedDateTime(shown.assignedAt)}${
                      shown.assignedBy ? ` by ${shown.assignedBy}` : ''
                    }`
                  : 'Not recorded',
              )}
            </div>
          </section>
          {open && canManage && auth?.currentUser?.isAdmin && (
            <section className={classes.section}>
              <h3 className={classes.heading}>Avigilon attachments</h3>
              <p className={classes.muted}>
                Check each reader’s actual site, zone and entry before testing
                access.
              </p>
              {shown.readers.map((reader) => (
                <ReaderAttachments
                  key={`${siteId}:${shown.assignmentId}:${reader.readerDeviceId}`}
                  siteId={siteId}
                  assignmentId={shown.assignmentId}
                  readerDeviceId={reader.readerDeviceId}
                  name={reader.name}
                  disabled={!actionsEnabled}
                />
              ))}
            </section>
          )}
          <section className={classes.section}>
            <h3 className={classes.heading}>Ports</h3>
            <p className={classes.muted}>
              {shown.ports.length
                ? shown.ports
                    .map(
                      (port) =>
                        `${port.label}: ${
                          shown.readers.find(
                            ({ readerId }) => readerId === port.readerId,
                          )?.name ?? 'empty'
                        }`,
                    )
                    .join(' · ')
                : 'Avigilon reported no ports.'}
            </p>
          </section>
          <section className={classes.section}>
            <h3 className={classes.heading}>Readers</h3>
            {shown.readers.length ? (
              <Table
                columns={readerColumns}
                data={shown.readers}
                remote
                disableGlobalFilter
                disableSortBy
              />
            ) : (
              <p className={classes.muted}>
                No readers belong to this controller.
              </p>
            )}
          </section>
        </div>
      </Modal>
      <Modal
        open={confirmingRemoval}
        setOpen={(next) => !next && setConfirmingRemoval(false)}
        title={`Remove ${shown.name} from this site?`}
        size="sm"
        actionText="Remove controller"
        actionButtonTheme="danger"
        actionButtonEnabled={actionsEnabled && !unresolvedQuery.loading}
        onAction={runRemove}
        cancelText="Keep it"
        onCancel={() => setConfirmingRemoval(false)}
      >
        <div className="odin-space-y-3 odin-text-sm odin-text-gray-600">
          {unresolvedQuery.error && (
            <Alert
              type="warn"
              text="Could not check the site for readers with no recorded controller; the removal may still be refused."
            />
          )}
          {blockers.length > 0 && (
            <Alert
              type="warn"
              text={`This removal will be refused: ${blockers.join(' ')}`}
            />
          )}
          {Array.from(
            new Map(
              (unresolvedQuery.data?.siteUnresolvedReaders ?? []).flatMap(
                ({ controller: on }) =>
                  on ? [[on.assignmentId, on] as const] : [],
              ),
            ).values(),
          ).map((on) => (
            <Button
              key={on.assignmentId}
              text={`Sync ${on.name}`}
              size="sm"
              theme="white"
              disabled={!actionsEnabled}
              onClick={() => {
                setConfirmingRemoval(false);
                return runSync(on.assignmentId, on.name);
              }}
            />
          ))}
          <p>
            {readersLeaving.length
              ? `${
                  readersLeaving.length
                } reader(s) leave the site with it: ${readersLeaving
                  .map(({ name }) => name)
                  .join(', ')}.`
              : 'No readers leave with it.'}
          </p>
          <p>
            Their draft placements are removed. A reader placed in the published
            layout stops the removal; unplace it and publish first. The hardware
            and its Avigilon connection stay recorded, and nothing is written to
            Avigilon.
          </p>
        </div>
      </Modal>
    </>
  );
}
